import React from "react";
import {AggregatedVisualizationResultRow} from "./ApiModels";
import {Badge, Button, Table} from "react-bootstrap";
import {CloudDownload} from "react-bootstrap-icons";
import {TableColumnFormatter} from "../CommonFunc/TableColumnFormatter";
import {saveAs} from "file-saver";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { ConsoleLogger } from "aws-amplify/utils";

const logger = new ConsoleLogger("TanStackAggregationReportTable.tsx")

export const TanStackAggregationReportTable: React.FC<{
    data: AggregatedVisualizationResultRow[];
    columns: ColumnDef<AggregatedVisualizationResultRow, any>[];
    isSelectedRows: boolean;
    onRowSelect: (rows: AggregatedVisualizationResultRow[], isSelect: boolean) => void;
    selectedRowsCount: number;
}> = ({ data, columns, isSelectedRows, onRowSelect, selectedRowsCount }) => {
    const [columnVisibility, setColumnVisibility] = React.useState<Record<string, boolean>>(() => {
            const initialVisibility: Record<string, boolean> = {}
            columns.forEach(col => {
                const key = col.id as string
                initialVisibility[key] = !col.meta?.hidden
            })
            return initialVisibility
        }
    )
    const prevSelectionCount = React.useRef<number>(0)
    const [rowSelection, setRowSelection] = React.useState<Record<string, boolean>>({})

    // Get ID's of all selected rows
    const selectedRowIds = React.useMemo(() => {
        return Object.keys(rowSelection).filter(id => rowSelection[id])
    }, [rowSelection])

    // Handle row selection
    React.useEffect(() => {
        const currentSelectionCount = selectedRowIds.length

        if (currentSelectionCount === 0 && prevSelectionCount.current > 0) onRowSelect([], false)
        else if (currentSelectionCount > 0) {
            const selectedRows = data.filter(row => selectedRowIds.includes(String(row.id)))
            logger.debug("selectedRows = ", selectedRows)
            onRowSelect(selectedRows, true)
        }

        prevSelectionCount.current = currentSelectionCount
    }, [selectedRowIds.length, data])

    // Handle select all separately
    const handleSelectAll = (isSelected: boolean) => {
        if (!isSelected) {
            setRowSelection({})
            onRowSelect([], false)
        }
    }

    // Add selection column if rows are selectable
    const selectionColumn: ColumnDef<AggregatedVisualizationResultRow, any> = {
        id: 'selectionColumn',
        header: ({ table }) => (
            <div className="d-flex justify-content-center">
                <input
                    type="checkbox"
                    className="form-check-input"
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                    checked={table.getIsAllRowsSelected()}
                    onChange={(e) => {
                        table.toggleAllRowsSelected(e.target.checked)
                        handleSelectAll(e.target.checked)
                    }}
                    onClick={(e) => e.stopPropagation()}
                />
            </div>
        ),
        cell: ({row}) => (
            <div className="d-flex justify-content-center" onClick={(e) => e.stopPropagation()}>
                <input
                    type="checkbox"
                    className="form-check-input"
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    onClick={(e) => e.stopPropagation()}
                />
            </div>
        ),
        enableSorting: false,
        enableColumnFilter: false,
    }

    const tableColumns = React.useMemo(() => {
        return isSelectedRows ? [selectionColumn, ...columns] : columns
    }, [columns, isSelectedRows])

    const table = useReactTable({
        data,
        columns: tableColumns,
        state: {
            rowSelection,
            columnVisibility,
        },
        enableRowSelection: isSelectedRows,
        onRowSelectionChange: setRowSelection,
        onColumnVisibilityChange: setColumnVisibility,
        getSortedRowModel: getSortedRowModel(),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        enableSorting: true,
        enableMultiRowSelection: true,
    })

    const noData = table.getRowModel().rows.length === 0

    const handleExportCSV = () => {
        const visibleCols = table.getVisibleFlatColumns()
            .filter(col => col.id !== 'selectionColumn')
            .map(col => col.id)
        const csvRows = [visibleCols.join(',')]

        table.getFilteredRowModel().rows.forEach(row => {
            const values = row.getVisibleCells()
                .filter(cell => cell.column.id !== 'selectionColumn')
                .map(cell => {
                const value = cell.getValue()
                return typeof value === 'boolean' ? (value ? 'true' : 'false') : JSON.stringify(value ?? '')
            })
            csvRows.push(values.join(','))
        })

        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8' })
        saveAs(blob, 'export.csv')
    }

    return (
        <>
            <div className="mb-2 d-flex justify-content-between align-items-center">
                <p className="mb-0">
                    Scroll down to see <Badge bg={"success"}>{selectedRowsCount}</Badge> selected graphs
                </p>
                <Button
                    className="btn-success btn-sm"
                    onClick={handleExportCSV}
                >
                    <CloudDownload/>
                </Button>
            </div>

            <div className="table-responsive table-small-medium-text bg-light">
                <Table bordered hover>
                    <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <th key={header.id}
                                    style={{cursor: header.column.getCanSort() ? 'cursor-pointer' : ''}}
                                >
                                    <div className='d-flex justify-content-between align-items-center w-100'>
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        {header.column.getCanSort() && (
                                            <span className="align-self-end mt-1"
                                                  onClick={header.column.getToggleSortingHandler()}
                                                  style={{
                                                      display: 'inline-block',
                                                      marginLeft: '4px',
                                                      cursor: 'pointer'
                                                  }}
                                            >
                                                {{
                                                    asc: <span style={{color: 'darkgreen'}}>▲</span>,
                                                    desc: <span style={{color: 'darkgreen'}}>▼</span>
                                                }[header.column.getIsSorted() as string] || '▼'}
                                            </span>
                                        )}
                                    </div>
                                    {/* Render column filters */}
                                    {header.column.getCanFilter() && header.column.columnDef.enableColumnFilter && (
                                        <div className="mt-2">
                                            <input
                                                type="text"
                                                value={header.column.getFilterValue() as string || ''}
                                                onChange={e => header.column.setFilterValue(e.target.value)}
                                                placeholder='Filter'
                                                className={`form-control form-control-sm ${
                                                    TableColumnFormatter.renderCssClassForFilter(columns.length)
                                                }`}
                                            />
                                        </div>
                                    )}
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody>
                        {noData ? (
                            <tr>
                                <td colSpan={columns.length}>
                                    <h6 className={"text-primary"}>No data found!</h6>
                                </td>
                            </tr>
                        ) : (
                            table.getRowModel().rows.map((row) => (
                                <tr key={row.id}
                                    onClick={() => {
                                        logger.debug("row = ", row)
                                        if (isSelectedRows) {
                                            row.toggleSelected()
                                        }
                                    }}
                                    className={row.getIsSelected() ? 'table-primary' : ''}
                                >
                                    {row.getVisibleCells().map(cell => (
                                        <td key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </div>
        </>
    )
}